import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, fmtUSD, fmtPct } from "../lib/api";
import {
  Calculator, ChartLine, Brain, Sparkle, Lightning, Target, PencilSimple, FloppyDisk, ArrowsClockwise,
  Eye, Compass, ShieldStar, MagnifyingGlass, Package, TrendUp, TrendDown,
} from "@phosphor-icons/react";

export default function SourcingModal({ open, onOpenChange, mode, target }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pkg, setPkg] = useState(null);

  useEffect(() => {
    if (!open || !target || !mode) return;
    setData(null);
    setError(null);
    setPkg(null);
    // Should-Cost activates the Doer tier (Strategic Partner Negotiation Playbook).
    // Rate Benchmark stays on the analytical lever for now.
    const call = mode === "SHOULD_COST"
      ? api.shouldCostDoer(target.po_id)
      : api.rateBenchmark(target.po_id);
    call.then(setData).catch((e) => setError(e?.message || "Lever unavailable"));
    if (target.segment_code) {
      api.categoryPackage(target.segment_code).then(setPkg).catch(() => setPkg(null));
    }
  }, [open, mode, target]);

  if (!mode) return null;

  const Icon = mode === "SHOULD_COST" ? Calculator : ChartLine;
  const titleText = mode === "SHOULD_COST" ? "Should-Cost Strategy" : "Rate Benchmark Strategy";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`sourcing-modal-${mode.toLowerCase()}`}
        className="max-w-4xl bg-[#F9FAFB] border border-[#D1D5DB] text-slate-950 p-0 max-h-[92vh] overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-[#D1D5DB] bg-[#F3F4F6]">
          <DialogTitle className="font-display text-lg flex items-center gap-2 text-slate-950 tracking-tight">
            <Icon weight="duotone" className="text-emerald-800" size={20} />
            Sourcing Strategy · {titleText}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-700 font-mono mt-1 font-medium">
            {target?.po_number} · {target?.supplier} · {target?.agency}
          </DialogDescription>
          <div className="mt-2 text-[11px] font-mono text-emerald-900 tracking-mono-label font-bold">
            Proposed by Strategic Partner · Awaiting Strategic Lead Refinement
          </div>
        </DialogHeader>

        <div className="overflow-auto thin-scroll max-h-[calc(92vh-80px)] p-6 space-y-5">
          {error && <div className="text-rose-800 text-sm font-medium" data-testid="sourcing-error">{error}</div>}
          {!data && !error && <Skeleton mode={mode} />}

          {data && (
            <>
              {/* ⓪ Category Data Package · Price Movement Drivers (TOP) */}
              {pkg && <DataPackageBand pkg={pkg} />}

              {/* ⓪ Strategic Negotiation Playbook (Strategic Partner Doer · Should-Cost only) */}
              {mode === "SHOULD_COST" && data.playbook && (
                <NegotiationPlaybook playbook={data.playbook} target={target} />
              )}

              {/* ① Procureo Agent Roster — show the army at work */}
              <AgentRoster mode={mode} />

              {/* ② SME analytical body — Market Signals · Vendor Signals */}
              {mode === "SHOULD_COST"
                ? <ShouldCostBody data={data} target={target} />
                : <RateBenchmarkBody data={data} target={target} />}

              {/* ③ Macro indicators chip strip */}
              <MacroIndicators items={data.macro_indicators || []} />

              {/* ④ SME findings note */}
              <SmeFindings text={data.sme_findings} />

              {/* ⑤ Action Recommendation — editable Partner Refinement (LAST) */}
              <ActionRecommendation action={data.action_recommendation} target={target} mode={mode} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Category Data Package · compact Price-Movement-Drivers band rendered at the
// TOP of the Maneuver Modal so the Strategic Lead sees the macro narrative
// before any reasoning text.
function DataPackageBand({ pkg }) {
  const drivers = (pkg.price_movement || []).slice(0, 4);
  if (drivers.length === 0) return null;
  return (
    <div
      data-testid="sourcing-data-package"
      className="border border-[#D1D5DB] bg-white rounded-sm p-4"
    >
      <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package weight="duotone" className="text-emerald-800" size={18} />
          <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
            Category Data Package · Price Movement Drivers
          </span>
        </div>
        <span className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label">
          {pkg.segment_code} · {pkg.segment_name || pkg.category}
        </span>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="sourcing-drivers-list">
        {drivers.map((d, i) => {
          const up = (d.mom_pct ?? 0) >= 0;
          const trendDown = d.trend === "falling";
          const TrendIcon = trendDown ? TrendDown : TrendUp;
          const accent = trendDown
            ? "border-emerald-700/40 bg-emerald-50  text-emerald-900"   // softer prices = good
            : up
              ? "border-rose-700/40    bg-rose-50      text-rose-900"   // rising prices = pressure
              : "border-slate-700/40   bg-white        text-slate-900";
          return (
            <li
              key={i}
              data-testid={`sourcing-driver-${d.code}`}
              className={`border ${accent} rounded-sm p-3 flex items-start gap-3`}
            >
              <TrendIcon weight="bold" size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-display text-[12.5px] font-bold text-slate-950 truncate">
                    {d.label}
                  </span>
                  <span className="font-mono text-[10px] text-slate-800 font-bold tabular-nums whitespace-nowrap">
                    {(d.mom_pct ?? 0) >= 0 ? "+" : ""}{(d.mom_pct ?? 0).toFixed(2)}% MoM
                  </span>
                </div>
                <div className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label mt-0.5">
                  {d.source} · {d.code}
                </div>
                {d.narrative && (
                  <p className="text-[11.5px] text-slate-900 mt-1 leading-snug font-medium">
                    {d.narrative}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
function ActionRecommendation({ action, target, mode }) {
  // Compose the AI-proposed strategy text (executable script) from the action payload
  const proposed = React.useMemo(() => composeProposed(action), [action]);
  const [refined, setRefined] = useState(proposed);
  const [savingState, setSavingState] = useState({ saving: false, savedAt: null, error: null });
  const [committed, setCommitted] = useState(null); // last committed text from Mongo

  // Hydrate any previously committed refinement from the Context Ledger
  useEffect(() => {
    let cancel = false;
    if (!target?.po_id || !mode) return;
    api.getRefinement(target.po_id)
      .then((r) => {
        if (cancel) return;
        const m = (r.refinements || []).find((d) => d.mode === mode);
        if (m?.refined_strategy) {
          setRefined(m.refined_strategy);
          setCommitted(m.refined_strategy);
          setSavingState({ saving: false, savedAt: m.committed_at, error: null });
        } else {
          setRefined(proposed);
        }
      })
      .catch(() => setRefined(proposed));
    return () => { cancel = true; };
  }, [target?.po_id, mode, proposed]);

  if (!action) return null;

  const dirty = (refined || "").trim() !== (committed || proposed || "").trim();

  const resetToProposed = () => {
    setRefined(proposed);
  };

  const commitToCop = async () => {
    if (!target?.po_id || !mode) return;
    setSavingState({ saving: true, savedAt: null, error: null });
    try {
      const r = await api.saveRefinement(target.po_id, {
        mode,
        refined_strategy: refined,
        proposed_strategy: proposed,
        author: "Strategic Lead",
      });
      setCommitted(refined);
      setSavingState({ saving: false, savedAt: r.committed_at, error: null });
      toast.success("Strategy Synchronized", {
        description: `Partner Refinement committed to the COP · ${target?.po_number}`,
        duration: 4500,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Sync failed";
      setSavingState({ saving: false, savedAt: null, error: msg });
      toast.error("Sync Failed", {
        description: msg,
        duration: 5000,
      });
    }
  };

  return (
    <div
      data-testid="action-recommendation"
      className="border border-fuchsia-500/40 bg-fuchsia-500/5 rounded-sm p-4"
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Brain weight="duotone" className="text-fuchsia-400" size={18} />
          <span className="tracking-mono-label text-[10px] text-fuchsia-300">
            Action Recommendation · drafted by the Negotiation Strategist
          </span>
        </div>
        <span className="font-mono text-[10px] text-slate-500 tracking-mono-label inline-flex items-center gap-1">
          <PencilSimple size={11} weight="bold" /> Partner Refinement · editable
        </span>
      </div>

      {action.verdict && (
        <div className="text-slate-100 font-medium leading-snug mb-3">
          {action.verdict}
        </div>
      )}

      {/* Editable Partner Refinement textarea */}
      <div className="bg-slate-950 border border-fuchsia-500/30 rounded-sm p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="tracking-mono-label text-[10px] text-fuchsia-300 inline-flex items-center gap-1">
            <PencilSimple size={11} weight="bold" /> Partner Refinement
          </span>
          {dirty && (
            <span className="font-mono text-[10px] text-amber-300 tracking-mono-label" data-testid="refinement-dirty">
              · unsaved edits
            </span>
          )}
        </div>
        <textarea
          data-testid="partner-refinement-input"
          value={refined}
          onChange={(e) => setRefined(e.target.value)}
          rows={Math.min(14, Math.max(6, (refined?.split("\n").length || 1) + 1))}
          spellCheck={true}
          className="w-full bg-slate-950 border border-slate-800 focus:border-fuchsia-500/60 outline-none
                     rounded-sm p-3 text-sm text-slate-50 leading-relaxed font-mono whitespace-pre-wrap resize-y"
          placeholder="Edit, delete, or rewrite the negotiation script. The Strategic Lead has final authority."
        />
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <div className="font-mono text-[10px] text-slate-500">
            {savingState.savedAt && !savingState.error && (
              <span data-testid="refinement-saved-stamp">
                Last committed · {new Date(savingState.savedAt).toLocaleString()}
              </span>
            )}
            {savingState.error && (
              <span className="text-rose-300" data-testid="refinement-error">{savingState.error}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="refinement-reset"
              onClick={resetToProposed}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-slate-700 bg-slate-900
                         font-mono text-[11px] tracking-mono-label text-slate-300 hover:border-slate-500 transition-colors"
            >
              <ArrowsClockwise size={11} weight="bold" />
              Reset to Proposed
            </button>
            <button
              data-testid="refinement-commit"
              onClick={commitToCop}
              disabled={savingState.saving}
              style={{
                backgroundColor: "#0D9488",
                color: "#FFFFFF",
                cursor: savingState.saving ? "wait" : "pointer",
                boxShadow: "0 2px 10px -2px rgba(6,95,70,0.55)",
              }}
              onMouseOver={(e) => { if (!savingState.saving) e.currentTarget.style.backgroundColor = "#064E3B"; }}
              onMouseOut={(e) => { if (!savingState.saving) e.currentTarget.style.backgroundColor = "#0D9488"; }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm
                         font-display text-[12px] font-bold tracking-wide transition-colors"
              title="Commit your refinement (or approve the proposal as-is) to the COP"
            >
              <FloppyDisk size={13} weight="bold" />
              {savingState.saving ? "Syncing…" : "Commit & Sync to COP"}
            </button>
          </div>
        </div>
      </div>

      {action.leverage_points?.length > 0 && (
        <div className="mb-3">
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-1.5">Leverage Points · context only</div>
          <ul className="space-y-1.5 text-xs text-slate-300">
            {action.leverage_points.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-mono text-fuchsia-400 shrink-0">▸</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
        {action.timing && (
          <div className="border border-slate-800 bg-slate-950 rounded-sm p-2.5">
            <div className="tracking-mono-label text-[10px] text-slate-500 mb-1 inline-flex items-center gap-1">
              <Target weight="bold" size={10} /> Timing
            </div>
            <div className="text-slate-200">{action.timing}</div>
          </div>
        )}
        {action.macro_basis?.length > 0 && (
          <div className="border border-slate-800 bg-slate-950 rounded-sm p-2.5">
            <div className="tracking-mono-label text-[10px] text-slate-500 mb-1">Macro Basis</div>
            <div className="text-slate-200 font-mono text-[10px] leading-relaxed">
              {action.macro_basis.join(" · ")}
            </div>
          </div>
        )}
      </div>

      {action.opus_trace && (
        <details className="mt-3" data-testid="opus-trace-toggle">
          <summary className="cursor-pointer text-[10px] tracking-mono-label text-fuchsia-300 hover:text-fuchsia-200">
            ▸ View Negotiation Strategist reasoning trace
          </summary>
          <p className="mt-2 font-mono text-[11px] text-slate-300 whitespace-pre-wrap leading-snug border-l-2 border-fuchsia-500/40 pl-3">
            {action.opus_trace}
          </p>
        </details>
      )}
    </div>
  );
}

// Compose the AI-proposed strategy text from the action payload so the
// Strategic Lead has an editable starting point that captures EXECUTE +
// leverage points in one cohesive script.
function composeProposed(action) {
  if (!action) return "";
  const parts = [];
  if (action.action) parts.push(action.action.trim());
  if (action.leverage_points?.length) {
    parts.push("");
    parts.push("Leverage points to weave into the conversation:");
    action.leverage_points.forEach((p) => parts.push(`  • ${p}`));
  }
  if (action.timing) {
    parts.push("");
    parts.push(`Timing: ${action.timing}`);
  }
  return parts.join("\n");
}

function ShouldCostBody({ data, target }) {
  const target_sc = Number(data.target_should_cost ?? 0);
  const realized  = Number(target?.realized ?? 0);
  const gap = Number(data.gap_vs_realized ?? (realized - target_sc));
  const gapPct = target_sc > 0 ? (gap / target_sc) * 100 : 0;
  return (
    <div data-testid="sme-should-cost" className="border border-emerald-500/30 bg-emerald-500/[0.03] rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle weight="duotone" className="text-emerald-400" size={16} />
        <span className="tracking-mono-label text-[10px] text-emerald-300">
          Technical Agent · Analysis · should-cost decomposition
        </span>
      </div>

      <div className="grid grid-cols-3 gap-px bg-slate-800 rounded-sm overflow-hidden mb-4">
        <Stat label="Realized" value={fmtUSD(realized)} />
        <Stat label="Should-Cost Target" value={fmtUSD(target_sc)} accent="emerald" />
        <Stat
          label="Gap vs Target"
          value={`${fmtUSD(Math.abs(gap))} (${fmtPct(gapPct, 1)})`}
          accent={gap > 0 ? "rose" : "emerald"}
        />
      </div>

      <ul className="space-y-2">
        {(data.decomposition || []).map((b, i) => (
          <li key={i} className="border border-slate-800 bg-slate-950 rounded-sm p-3">
            <div className="flex items-baseline justify-between mb-1.5 gap-2">
              <span className="text-slate-100 font-medium truncate">{b.bucket}</span>
              <span className="font-mono text-sm text-emerald-300 shrink-0">
                {fmtUSD(Number(b.value || 0))} <span className="text-slate-500 text-[10px]">({Number(b.pct || 0).toFixed(1)}%)</span>
              </span>
            </div>
            <div className="h-1.5 bg-slate-800 overflow-hidden rounded-sm">
              <div className="h-full bg-emerald-500/70" style={{ width: `${Math.min(100, Number(b.pct||0))}%` }} />
            </div>
            {b.basis && (
              <div className="mt-2 text-[11px] text-slate-400 font-mono">
                <span className="text-emerald-400/80">basis:</span> {b.basis}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RateBenchmarkBody({ data, target }) {
  const benchmarks = data.benchmarks || [];
  const realizedRate = Number(data.realized_unit_rate ?? 0);
  const variancePct = Number(data.variance_vs_median_pct ?? 0);
  const percentile = Number(data.percentile ?? 50);

  const lows  = benchmarks.map(b => Number(b.low || 0));
  const highs = benchmarks.map(b => Number(b.high || 0));
  const min = Math.min(realizedRate, ...lows);
  const max = Math.max(realizedRate, ...highs);
  const range = max - min || 1;

  return (
    <div data-testid="sme-rate-benchmark" className="border border-emerald-500/30 bg-emerald-500/[0.03] rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle weight="duotone" className="text-emerald-400" size={16} />
        <span className="tracking-mono-label text-[10px] text-emerald-300">
          Technical Agent · Analysis · rate benchmark
        </span>
      </div>

      <div className="grid grid-cols-3 gap-px bg-slate-800 rounded-sm overflow-hidden mb-4">
        <Stat label="Realized Unit Rate" value={fmtUSD(realizedRate)} />
        <Stat
          label="Percentile"
          value={`${percentile.toFixed(0)} / 100`}
          accent={percentile <= 50 ? "emerald" : "rose"}
        />
        <Stat
          label="Variance vs Median"
          value={`${fmtPct(variancePct, 1)}`}
          accent={variancePct <= 0 ? "emerald" : "rose"}
        />
      </div>

      <ul className="space-y-2">
        {benchmarks.map((b, i) => {
          const low = Number(b.low || 0);
          const median = Number(b.median || 0);
          const high = Number(b.high || 0);
          const left = ((low - min) / range) * 100;
          const width = ((high - low) / range) * 100;
          const medianPos = ((median - min) / range) * 100;
          const realizedPos = ((realizedRate - min) / range) * 100;
          return (
            <li key={i} className="border border-slate-800 bg-slate-950 rounded-sm p-3">
              <div className="flex items-baseline justify-between mb-2 gap-2">
                <span className="font-mono text-[11px] text-emerald-400 tracking-wide truncate">{b.source}</span>
                <span className="font-mono text-[11px] text-slate-400 shrink-0">
                  {fmtUSD(low)} – {fmtUSD(high)}
                </span>
              </div>
              <div className="relative h-3 bg-slate-800 rounded-sm">
                <div
                  className="absolute top-0 h-full bg-emerald-500/30 rounded-sm"
                  style={{ left: `${left}%`, width: `${Math.max(width, 1)}%` }}
                />
                <div
                  className="absolute top-0 h-full w-px bg-emerald-300"
                  style={{ left: `${medianPos}%` }}
                  title={`Median: ${fmtUSD(median)}`}
                />
                <div
                  className="absolute -top-0.5 h-4 w-0.5 bg-rose-400"
                  style={{ left: `${realizedPos}%` }}
                  title={`Realized: ${fmtUSD(realizedRate)}`}
                />
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-4 mt-2 text-[10px] font-mono text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 bg-emerald-500/30 rounded-sm" /> Low–High</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-px bg-emerald-300" /> Median</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-0.5 bg-rose-400" /> Realized</span>
      </div>
    </div>
  );
}

function MacroIndicators({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="macro-indicators">
      <div className="tracking-mono-label text-[10px] text-slate-500 mb-2">
        Macro Indicators · cited by SME tier
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((m, i) => (
          <span
            key={i}
            className="font-mono text-[10px] text-sky-300 bg-sky-500/10 border border-sky-500/30 px-2 py-1 rounded-sm"
          >
            {m}
          </span>
        ))}
      </div>
    </div>
  );
}

function SmeFindings({ text }) {
  if (!text) return null;
  return (
    <div data-testid="sme-findings" className="border-l-2 border-emerald-500/60 pl-3 py-1">
      <div className="tracking-mono-label text-[10px] text-emerald-400">SME Findings</div>
      <p className="text-xs text-slate-300 mt-1 leading-relaxed">{text}</p>
    </div>
  );
}

function AgentRoster({ mode }) {
  const agents = mode === "SHOULD_COST"
    ? [
        { name: "Commodity Observer",  role: "monitors raw-material & labor indices",   icon: Eye },
        { name: "PDI Sentinel",        role: "watches counterparty PDI + filings",      icon: ShieldStar },
        { name: "Should-Cost Doer",    role: "decomposes the realized award stack",     icon: Calculator },
        { name: "Negotiation Strategist", role: "drafts the recommendation script",     icon: Brain },
      ]
    : [
        { name: "Rate-Card Analyst",   role: "benchmarks against peer awards",          icon: ChartLine },
        { name: "PDI Sentinel",        role: "watches counterparty PDI + filings",      icon: ShieldStar },
        { name: "Compliance Auditor",  role: "checks audit-clean evidence chain",       icon: MagnifyingGlass },
        { name: "Negotiation Strategist", role: "drafts the parity-ask script",         icon: Brain },
      ];
  return (
    <div
      data-testid="agent-roster"
      className="border border-slate-800 bg-slate-950 rounded-sm p-3"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="tracking-mono-label text-[10px] text-slate-400 inline-flex items-center gap-1.5">
          <Compass weight="duotone" size={11} /> Procureo Agents on this maneuver
        </span>
        <span className="font-mono text-[10px] text-slate-500">
          {agents.length} agents in the trace
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {agents.map((a) => (
          <div
            key={a.name}
            data-testid={`agent-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex items-start gap-2 border border-slate-800 bg-slate-900 rounded-sm p-2"
          >
            <a.icon weight="duotone" size={16} className="text-emerald-400 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-display text-[12px] font-semibold text-slate-100 leading-tight truncate">
                {a.name}
              </div>
              <div className="font-mono text-[10px] text-slate-500 leading-snug truncate">
                {a.role}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const color = {
    slate: "text-slate-100",
    emerald: "text-emerald-400",
    rose: "text-rose-400",
  }[accent];
  return (
    <div className="bg-slate-950 p-3">
      <div className="tracking-mono-label text-[10px] text-slate-500">{label}</div>
      <div className={`font-mono text-base ${color} mt-1`}>{value}</div>
    </div>
  );
}

function Skeleton({ mode }) {
  return (
    <div className="space-y-3" data-testid={`sourcing-loading-${mode.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono">
        <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
        Procureo pipeline executing · Technical Agent → Negotiation Strategist
      </div>
      <div className="h-24 skeleton rounded-sm" />
      <div className="h-32 skeleton rounded-sm" />
      <div className="h-20 skeleton rounded-sm" />
    </div>
  );
}


// ─── Strategic Negotiation Playbook (Strategic Partner Doer · Should-Cost) ────────────
function NegotiationPlaybook({ playbook, target }) {
  if (!playbook) return null;
  const gapPct  = Number(playbook.value_gap_pct || 0);
  const gapUSD  = Number(playbook.value_gap_usd || 0);
  const barPct  = Math.min(100, Math.abs(gapPct) * 2);
  const buyer   = gapPct >= 0;
  const m       = playbook.fused_signals?.market || [];
  const v       = playbook.fused_signals?.vendor || [];
  const maneuver = playbook.maneuver || {};
  return (
    <div
      data-testid="negotiation-playbook"
      className="border border-fuchsia-500/30 bg-fuchsia-500/[0.04] rounded-sm p-4 shadow-[0_0_32px_-12px_rgba(217,70,239,0.45)]"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Brain weight="duotone" className="text-fuchsia-300" size={18} />
          <span className="tracking-mono-label text-[10px] text-fuchsia-200">
            Strategic Negotiation Playbook · drafted by the Negotiation Strategist
          </span>
        </div>
        <span className="font-mono text-[10px] text-slate-500">
          {target?.po_number} · ready to execute
        </span>
      </div>

      <p
        data-testid="playbook-executive-summary"
        className="text-sm text-slate-100 leading-relaxed mt-3"
      >
        {playbook.executive_summary}
      </p>

      <div
        data-testid="playbook-value-gap"
        className="mt-4 border border-slate-800 bg-slate-950 rounded-sm p-3"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="tracking-mono-label text-[10px] text-emerald-300">Value Gap</span>
          <span className={`font-mono text-[11px] ${buyer ? "text-emerald-300" : "text-rose-300"}`}>
            {gapPct >= 0 ? "+" : ""}{gapPct.toFixed(2)}% · {fmtUSD(gapUSD)}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <span className="font-mono text-[10px] text-slate-500 w-20 text-right">should-cost</span>
          <div className="flex-1 relative h-3 bg-slate-800 rounded-sm overflow-hidden">
            <div
              className={`absolute inset-y-0 ${buyer ? "left-1/2" : "right-1/2"} ${buyer ? "bg-emerald-500/70" : "bg-rose-500/70"}`}
              style={{ width: `${barPct / 2}%` }}
            />
            <div className="absolute inset-y-0 left-1/2 w-px bg-slate-600" />
          </div>
          <span className="font-mono text-[10px] text-slate-500 w-20">realized</span>
        </div>
        <div className="mt-1 font-mono text-[9px] text-slate-500 text-center">
          {buyer
            ? "realized is above should-cost → opportunity to recapture shared value"
            : "realized is already below should-cost → defend the win"}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-800 rounded-sm overflow-hidden"
           data-testid="playbook-fused-signals">
        <div className="bg-slate-950 p-3">
          <div className="tracking-mono-label text-[10px] text-amber-300 mb-2">Market signals · FRED · BLS · ECB · GSA</div>
          <ul className="space-y-1.5 text-[11px]">
            {m.map((s, i) => (
              <li key={i} className="leading-snug" data-testid={`market-signal-${i}`}>
                <span className={`font-mono text-[10px] ${s.supports_buyer ? "text-emerald-300" : "text-rose-300"} mr-1.5`}>
                  {s.supports_buyer ? "▲" : "▼"}
                </span>
                <span className="font-mono text-[10px] text-amber-300">{s.source}·{s.code}</span>
                <span className="text-slate-200 ml-1.5">{s.label}</span>
                <div className="text-[10px] text-slate-400 leading-snug ml-4">{s.observation}</div>
              </li>
            ))}
            {m.length === 0 && <li className="text-slate-500 font-mono text-[10px]">— no market signals cited</li>}
          </ul>
        </div>
        <div className="bg-slate-950 p-3">
          <div className="tracking-mono-label text-[10px] text-sky-300 mb-2">Vendor signals · 10-K · backlog · public posture</div>
          <ul className="space-y-1.5 text-[11px]">
            {v.map((s, i) => (
              <li key={i} className="leading-snug" data-testid={`vendor-signal-${i}`}>
                <span className={`font-mono text-[10px] ${s.supports_buyer ? "text-emerald-300" : "text-rose-300"} mr-1.5`}>
                  {s.supports_buyer ? "▲" : "▼"}
                </span>
                <span className="text-slate-200">{s.signal}</span>
                <div className="text-[10px] text-slate-400 leading-snug ml-4">{s.observation}</div>
              </li>
            ))}
            {v.length === 0 && <li className="text-slate-500 font-mono text-[10px]">— no vendor signals cited</li>}
          </ul>
        </div>
      </div>

      <div className="mt-4 border-l-2 border-emerald-500/40 bg-emerald-500/5 rounded-sm p-3"
           data-testid="playbook-maneuver">
        <div className="flex items-center gap-2 mb-1.5">
          <Target weight="duotone" className="text-emerald-300" size={13} />
          <span className="tracking-mono-label text-[10px] text-emerald-300">Strategic Partner Maneuver</span>
        </div>
        <div className="text-[11px] font-mono text-slate-400">
          target outcome · <span className="text-slate-200">{maneuver.target_outcome || "—"}</span>
        </div>

        {maneuver.opening_script && (
          <blockquote
            data-testid="playbook-opening-script"
            className="mt-2 border-l-2 border-fuchsia-500/40 pl-3 py-1 text-[12px] text-slate-100 italic leading-snug"
          >
            "{maneuver.opening_script}"
          </blockquote>
        )}

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800 rounded-sm overflow-hidden text-[10px] font-mono">
          {maneuver.anchor_offer && (
            <div className="bg-slate-950 p-2">
              <div className="tracking-mono-label text-[9px] text-emerald-300">Anchor offer</div>
              <div className="text-slate-100 mt-0.5">{fmtUSD(maneuver.anchor_offer.amount_usd || 0)}</div>
              <div className="text-[9px] text-slate-500 mt-0.5">
                {(maneuver.anchor_offer.variance_to_realized_pct >= 0 ? "+" : "")}
                {Number(maneuver.anchor_offer.variance_to_realized_pct || 0).toFixed(1)}% vs realized
              </div>
            </div>
          )}
          {maneuver.walk_away_band && (
            <div className="bg-slate-950 p-2">
              <div className="tracking-mono-label text-[9px] text-amber-300">Walk-away band</div>
              <div className="text-slate-100 mt-0.5">
                {fmtUSD(maneuver.walk_away_band.floor_usd || 0)} → {fmtUSD(maneuver.walk_away_band.ceiling_usd || 0)}
              </div>
            </div>
          )}
          {(maneuver.counter_concessions || []).length > 0 && (
            <div className="bg-slate-950 p-2">
              <div className="tracking-mono-label text-[9px] text-sky-300">Non-price concessions</div>
              <ul className="text-slate-300 mt-0.5 leading-snug space-y-0.5">
                {maneuver.counter_concessions.slice(0, 4).map((c, i) => (
                  <li key={i}>· {c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {maneuver.if_pushback && (
          <div className="mt-3 text-[11px] text-slate-300 leading-snug"
               data-testid="playbook-pushback">
            <span className="tracking-mono-label text-[10px] text-amber-300">if pushback · </span>
            {maneuver.if_pushback}
          </div>
        )}
      </div>

      {playbook.opus_trace && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-[10px] tracking-mono-label text-fuchsia-300 hover:text-fuchsia-200">
            <Sparkle weight="bold" size={9} className="inline mr-1" /> Negotiation Strategist reasoning trace
          </summary>
          <p className="mt-2 text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap font-mono">
            {playbook.opus_trace}
          </p>
        </details>
      )}
    </div>
  );
}
